import { Challenge, Player } from '../types';
import { resolvePlaceholders } from './templateUtils';

// Check if an item is a challenge
export const isChallenge = (item: any): item is Challenge => {
  return item && typeof item === 'object' && item.id && item.id.startsWith('c_');
};

// Check if a challenge has placeholders
export const isChallengeTemplate = (challenge: Challenge): boolean => {
  return Array.isArray(challenge.placeholders) && challenge.placeholders.length > 0;
};

// Resolve challenge text with player names
export const resolveChallengeText = (challenge: Challenge, players: Player[]): {
  resolvedText: string;
  placeholdersResolved: Record<string, string>;
} => {
  if (!isChallengeTemplate(challenge)) {
    return {
      resolvedText: challenge.template,
      placeholdersResolved: {}
    };
  }

  return resolvePlaceholders(challenge.template, challenge.placeholders, players);
};

// Get challenges filtered by category and minimum players
export const filterChallengesByCategory = (
  challenges: Challenge[],
  categories: string[],
  minPlayers: number
): Challenge[] => {
  console.log('🔍 Filtering challenges:', {
    totalChallenges: challenges.length,
    categories,
    minPlayers,
    activeChallenges: challenges.filter(c => c.active).length
  });
  
  const filtered = challenges.filter(challenge =>
    challenge.active &&
    challenge.minPlayers <= minPlayers &&
    (categories.includes('all') || categories.some(cat => challenge.category === cat) || challenge.category === 'mix')
  );
  
  console.log('✅ Filtered challenges result:', {
    filtered: filtered.length,
    challengeCategories: filtered.map(c => c.category),
    challengeIds: filtered.map(c => c.id)
  });
  
  return filtered;
};

// Determine if this should be a challenge round (30% chance)
export const shouldBeChallenge = (roundIndex: number, totalRounds: number, targetPercentage: number = 30): boolean => {
  // Calculate how many challenges we should have based on the target percentage
  const targetChallenges = Math.max(1, Math.ceil((totalRounds * targetPercentage) / 100));
  
  // Ensure we don't have more challenges than total rounds
  const actualTargetChallenges = Math.min(targetChallenges, totalRounds - 1);
  
  // Use a deterministic approach to ensure we hit the target percentage
  // Spread challenges evenly throughout the game
  if (actualTargetChallenges <= 0) return false;
  
  const challengeInterval = Math.max(1, Math.floor(totalRounds / actualTargetChallenges));
  
  // Make sure at least one challenge appears, but not in the first round
  if (roundIndex === 0) return false;
  
  // For smaller games, use a simpler distribution
  if (totalRounds <= 5) {
    // In 5-round games, put challenge at round 3
    return roundIndex === 2;
  }
  
  // Check if this round should be a challenge based on interval
  const isIntervalRound = (roundIndex + 1) % challengeInterval === 0;
  
  // Count how many challenges we've already assigned
  const currentChallengeCount = Math.floor(roundIndex / challengeInterval);
  const canAddChallenge = currentChallengeCount < actualTargetChallenges;
  
  console.log(`Challenge check for round ${roundIndex}:`, {
    targetPercentage,
    totalRounds,
    targetChallenges,
    actualTargetChallenges,
    challengeInterval,
    isIntervalRound,
    currentChallengeCount,
    canAddChallenge,
    result: isIntervalRound && canAddChallenge
  });
  
  return isIntervalRound && canAddChallenge;
};